/**
 * Chart Widget API를 래핑하는 유틸리티 클래스
 * TradingView Chart Widget의 복잡한 API를 단순화하고 관리합니다.
 */
import { getThemeFromSettings, getThemeColors, getBidAskLineColors, generateThemeOverrides, generateThemeBackgroundOverrides, } from '../utils/ChartTheme';
import { needsFeaturesRecreation, generateAllOverrides } from '../utils/TradingViewFeatures';
import { buildWidgetConfig } from '../utils/ChartConfigBuilder';
import { generateChartManagerId } from '../utils/ChartUtils';
import { DEFAULT_ORDER_LINE_STYLE } from '@template/types';
import { setupChart } from '../utils/ChartSetupUtils';
import { useMetadataStore } from '@template/stores';
export class ChartManager {
    constructor(options) {
        Object.defineProperty(this, "widget", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "currentSymbol", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "currentInterval", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: '1'
        });
        Object.defineProperty(this, "currentSubscriberUID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "currentSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "instanceId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "webSocketService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "containerId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        // 주문 라인 관리
        Object.defineProperty(this, "orderLines", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "orderLineStyle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { ...DEFAULT_ORDER_LINE_STYLE }
        });
        // Bid/Ask 라인 관리 (심볼별로 bid/ask 라인 하나씩 유지)
        Object.defineProperty(this, "bidAskLines", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        /** Bid/Ask 라인 키별 비동기 작업 직렬화 잠금 */
        Object.defineProperty(this, "bidAskLineLocks", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        // 컨텍스트 메뉴 관리
        Object.defineProperty(this, "contextMenuCallbacks", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "loggedShapes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        }); // 로그 중복 방지
        /** 재연결 시 캔들 재로드를 위한 datafeed 참조 */
        Object.defineProperty(this, "datafeed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // 각 인스턴스마다 고유 ID 생성
        this.instanceId = generateChartManagerId();
        this.webSocketService = options?.webSocketService || null;
    }
    /**
     * Chart Widget 초기화
     * @param config - 차트 설정 및 quoteService가 포함되어 있으면 자동으로 datafeed 생성
     */
    async initializeChart(config) {
        if (this.isInitialized) {
            return;
        }
        this.containerId = config.container;
        this.currentSymbol = config.symbol;
        this.currentInterval = config.interval;
        this.currentSettings = config.settings || null;
        return new Promise(async (resolve, reject) => {
            try {
                await this.ensureTradingViewLoaded();
                // 위젯 설정 생성 및 초기 설정
                const widgetConfig = buildWidgetConfig(config);
                this.datafeed = widgetConfig.datafeed;
                // widgetConfig.interval이 최종적으로 사용되는 값이므로 이를 기준으로 설정
                // onChartReady 콜백에서 사용하기 위해 config.interval을 로컬 변수에 저장
                const expectedInterval = widgetConfig.interval || config.interval;
                this.widget = new window.TradingView.widget(widgetConfig);
                this.widget?.onChartReady(() => {
                    this.isInitialized = true;
                    // 기본 차트 설정 적용
                    setupChart(this.widget);
                    // expectedInterval이 있고 currentInterval과 다르면 재설정
                    const intervalToUse = expectedInterval || this.currentInterval;
                    if (intervalToUse && this.widget) {
                        try {
                            // interval 설정
                            this.widget.setSymbol(this.currentSymbol, intervalToUse);
                            // currentInterval 업데이트
                            this.currentInterval = intervalToUse;
                        }
                        catch (error) {
                            console.warn('[ChartManager] Failed to set interval in onChartReady:', error);
                        }
                    }
                    else {
                        console.warn('[ChartManager] onChartReady - intervalToUse is missing or widget is null:', {
                            intervalToUse,
                            expectedInterval,
                            currentInterval: this.currentInterval,
                            hasWidget: !!this.widget,
                        });
                    }
                    // 차트가 완전히 준비된 후 상세 설정 적용
                    this.applyPostLoadSettings();
                    resolve();
                });
            }
            catch (error) {
                console.error('[ChartManager] Failed to initialize chart:', error);
                reject(error);
            }
        });
    }
    /**
     * TradingView 라이브러리 로드 확인
     * HTML에서 미리 로드되므로 단순 확인만 수행
     */
    async ensureTradingViewLoaded() {
        if (!window.TradingView || !window.TradingView.widget) {
            throw new Error('TradingView 라이브러리가 로드되지 않았습니다. HTML에서 charting_library.standalone.js를 미리 로드해야 합니다.');
        }
    }
    /**
     * 심볼 변경
     */
    changeSymbol(symbol) {
        if (!this.widget) {
            console.warn('[ChartManager] Chart not initialized');
            return;
        }
        // 이전 심볼 구독 해제
        if (this.currentSymbol && this.currentSubscriberUID) {
            this.unsubscribeSymbol(this.currentSymbol);
        }
        this.currentSymbol = symbol;
        try {
            // 차트 심볼 변경 (TradingView가 자동으로 데이터 로드)
            this.widget.setSymbol(symbol, this.currentInterval);
        }
        catch (error) {
            console.error('[ChartManager] Symbol change failed:', error);
        }
    }
    /**
     * 간격 변경
     */
    changeInterval(interval) {
        if (!this.widget) {
            console.warn('[ChartManager] Chart not initialized');
            return;
        }
        this.currentInterval = interval;
        try {
            // TradingView에서 interval을 변경하는 올바른 방법은 setSymbol을 사용하는 것
            this.widget.setSymbol(this.currentSymbol, interval);
        }
        catch (error) {
            console.error('[ChartManager] Interval change failed:', error);
        }
    }
    /**
     * 차트 새로고침
     */
    refreshChart() {
        if (!this.widget) {
            console.warn('[ChartManager] Chart not initialized');
            return;
        }
        try {
            if (typeof this.widget.chart !== 'function') {
                console.warn('[ChartManager] Chart method not available');
                return;
            }
            const chart = this.widget.chart();
            if (chart && typeof chart.refresh === 'function') {
                chart.refresh();
            }
        }
        catch (error) {
            console.error('[ChartManager] Chart refresh failed');
        }
    }
    /**
     * 캔들 히스토리 재로드 (재연결 시 getBars → candle API 재호출)
     * 구독 해제 → resetCache → resetData → setSymbol 순서로 호출
     */
    reloadChartHistory() {
        try {
            if (!this.widget)
                return;
            const chart = this.widget.chart?.();
            const widgetWithReset = this.widget;
            this.datafeed?.unsubscribeCurrentBars?.();
            if (typeof widgetWithReset.resetCache === 'function')
                widgetWithReset.resetCache();
            if (chart && typeof chart.resetData === 'function')
                chart.resetData();
            this.widget.setSymbol(this.currentSymbol, this.currentInterval);
        }
        catch (error) {
            console.error('[ChartManager] reloadChartHistory failed:', error);
        }
    }
    /**
     * 볼륨 지표 제거
     */
    removeVolumeIndicator() {
        if (!this.widget) {
            console.warn('[ChartManager] Chart not initialized');
            return;
        }
        try {
            const chart = this.widget.chart();
            if (!chart || typeof chart.getAllStudies !== 'function') {
                console.warn('[ChartManager] Chart getAllStudies method not available');
                return;
            }
            // 볼륨 지표 제거
            const studies = chart.getAllStudies();
            const volumeStudy = studies.find((s) => s.name === 'Volume');
            if (volumeStudy && typeof chart.removeEntity === 'function') {
                chart.removeEntity(volumeStudy.id);
            }
        }
        catch (error) {
            console.error('[ChartManager] Volume indicator remove failed');
        }
    }
    /**
     * 테스트용 지표 추가
     */
    addTestIndicators() {
        if (!this.isInitialized || !this.widget) {
            console.warn('[ChartManager] Cannot add test indicators: chart not initialized');
            return;
        }
        try {
            const chart = this.widget.chart();
            if (!chart || typeof chart.createStudy !== 'function') {
                console.warn('[ChartManager] Chart createStudy method not available');
                return;
            }
            const theme = this.currentSettings ? getThemeFromSettings(this.currentSettings) : 'redBlue';
            const themeColors = getThemeColors(theme);
            const studyOverrides = {
                'volume.color.0': themeColors.up,
                'volume.color.1': themeColors.down,
                'volume.transparency': 50,
            };
            chart.createStudy('Volume', false, false, {}, null, studyOverrides);
        }
        catch (error) {
            console.warn('[ChartManager] Failed to add test indicators:', error);
        }
    }
    /**
     * 테스트용 지표 제거
     */
    removeAllIndicators() {
        if (!this.widget) {
            console.warn('[ChartManager] Cannot remove indicators: chart not initialized');
            return;
        }
        try {
            const chart = this.widget.chart();
            if (!chart || typeof chart.getAllStudies !== 'function') {
                console.warn('[ChartManager] Chart getAllStudies method not available');
                return;
            }
            const studies = chart.getAllStudies();
            const volumeStudy = studies.find((s) => s.name === 'Volume');
            if (volumeStudy && typeof chart.removeEntity === 'function') {
                chart.removeEntity(volumeStudy.id);
            }
        }
        catch (error) {
            console.warn('[ChartManager] Failed to remove test indicators:', error);
        }
    }
    /**
     * 지표 추가
     * @param indicatorId - TradingView 지표 이름 (예: 'Moving Average', 'RSI')
     * @returns 추가된 지표의 ID 또는 null
     */
    addIndicator(indicatorId) {
        if (!this.isInitialized || !this.widget) {
            console.warn('[ChartManager] Cannot add indicator: chart not initialized');
            return null;
        }
        try {
            const chart = this.widget.chart();
            if (!chart) {
                console.warn('[ChartManager] Chart instance not available');
                return null;
            }
            if (typeof chart.createStudy !== 'function') {
                console.warn('[ChartManager] Chart createStudy method not available');
                return null;
            }
            // 이미 동일한 지표가 있는지 확인
            if (this.hasIndicator(indicatorId)) {
                const existingIndicators = this.getAllIndicators();
                const existing = existingIndicators.find((indicator) => indicator.name === indicatorId);
                return existing?.id ?? null;
            }
            // 기본 제공 형태로 지표 추가 (TradingView 기본 설정 사용)
            const studyId = chart.createStudy(indicatorId, false, // overlay
            false, // lock
            {}, // inputs (기본값 사용)
            null, // callback
            {} // overrides (기본값 사용)
            );
            return studyId;
        }
        catch (error) {
            console.error('[ChartManager] Failed to add indicator:', indicatorId, error);
            return null;
        }
    }
    /**
     * 지표 제거
     * @param indicatorId - 제거할 지표의 ID
     */
    removeIndicator(indicatorId) {
        this.removeIndicatorByName(indicatorId);
    }
    /**
     * 지표 이름으로 지표 제거
     * @param indicatorName - TradingView 지표 이름
     * @returns 제거된 지표 ID 또는 null
     */
    removeIndicatorByName(indicatorId) {
        if (!this.widget) {
            console.warn('[ChartManager] Cannot remove indicator: chart not initialized');
            return null;
        }
        try {
            const chart = this.widget.chart();
            if (!chart || typeof chart.getAllStudies !== 'function') {
                console.warn('[ChartManager] Chart getAllStudies method not available');
                return null;
            }
            const studies = chart.getAllStudies();
            const indicatorIdLower = indicatorId.toLowerCase();
            let study = studies.find((study) => study.name.toLowerCase() === indicatorIdLower);
            if (study && typeof chart.removeEntity === 'function') {
                chart.removeEntity(study.id);
                return study.id;
            }
            console.warn('[ChartManager] Indicator not found or cannot remove:', indicatorId, {
                found: !!study,
                hasRemoveEntity: typeof chart.removeEntity === 'function',
                availableStudies: studies.map((s) => s.name),
            });
            return null;
        }
        catch (error) {
            console.error('[ChartManager] Failed to remove indicator by name:', indicatorId, error);
            return null;
        }
    }
    /**
     * 현재 적용된 모든 지표 가져오기
     * @returns 지표 목록
     */
    getAllIndicators() {
        if (!this.widget) {
            console.warn('[ChartManager] Cannot get indicators: chart not initialized');
            return [];
        }
        try {
            const chart = this.widget.chart();
            if (!chart || typeof chart.getAllStudies !== 'function') {
                console.warn('[ChartManager] Chart getAllStudies method not available');
                return [];
            }
            const studies = chart.getAllStudies();
            const indicators = studies.map((study) => ({
                id: String(study.id),
                name: study.name,
            }));
            return indicators;
        }
        catch (error) {
            console.warn('[ChartManager] Failed to get indicators:', error);
            return [];
        }
    }
    /**
     * 특정 지표가 이미 적용되어 있는지 확인
     * @param indicatorName - TradingView 지표 이름
     * @returns 지표가 적용되어 있으면 true
     */
    hasIndicator(indicatorId) {
        if (!this.widget) {
            return false;
        }
        try {
            const chart = this.widget.chart();
            if (!chart || typeof chart.getAllStudies !== 'function') {
                return false;
            }
            const studies = chart.getAllStudies();
            const indicatorIdLower = indicatorId.toLowerCase();
            return studies.some((study) => study.name.toLowerCase() === indicatorIdLower);
        }
        catch (error) {
            console.warn('[ChartManager] Failed to check indicator existence:', error);
            return false;
        }
    }
    /**
     * 차트 설정 적용 (overrides)
     */
    applyChartOverrides(overrides) {
        if (!this.widget || !this.isInitialized) {
            console.warn('[ChartManager] Chart is not initialized');
            return;
        }
        try {
            if (typeof this.widget.chart !== 'function') {
                console.warn('[ChartManager] Chart method not available');
                return;
            }
            const chart = this.widget.chart();
            if (chart && typeof chart.applyOverrides === 'function') {
                chart.applyOverrides(overrides);
            }
            else {
                console.warn('[ChartManager] Chart applyOverrides method not available');
            }
        }
        catch (error) {
            console.error('[ChartManager] Failed to apply chart overrides:', error);
        }
    }
    /**
     * 설정 기반 차트 업데이트
     */
    async applyChartSettings(settings) {
        if (!this.widget || !this.isInitialized) {
            console.warn('[ChartManager] Chart not initialized');
            return;
        }
        const oldSettings = this.currentSettings;
        this.currentSettings = settings;
        try {
            const metadataStore = useMetadataStore();
            const metadata = this.currentSymbol
                ? metadataStore.getMetadata(this.currentSymbol)
                : undefined;
            const pointUnit = metadata?.pointUnit;
            // 차트 오버라이드 적용
            const overrides = generateAllOverrides(settings, pointUnit);
            const theme = getThemeFromSettings(settings);
            const themeOverrides = generateThemeOverrides(theme);
            const allOverrides = { ...overrides, ...themeOverrides };
            this.applyChartOverrides(allOverrides);
            // 테마 배경 업데이트
            this.updateThemeBackground();
            // 테마 변경 시 bid/ask 라인 색상 업데이트
            if (oldSettings && oldSettings.basic.theme !== settings.basic.theme) {
                await this.updateAllBidAskLineColors();
            }
        }
        catch (error) {
            console.error('[ChartManager] Error applying chart settings:', error);
        }
    }
    /**
     * 현재 설정 가져오기
     */
    getCurrentSettings() {
        // 깊은 복사로 독립된 객체 반환 (참조 문제 방지)
        return this.currentSettings ? JSON.parse(JSON.stringify(this.currentSettings)) : null;
    }
    /**
     * 설정 변경으로 인한 차트 재생성이 필요한지 확인
     */
    needsRecreation(newSettings) {
        if (!this.currentSettings) {
            return true;
        }
        return needsFeaturesRecreation(this.currentSettings, newSettings);
    }
    /**
     * 설정을 TradingView overrides로 변환
     */
    convertSettingsToOverrides(settings) {
        // 현재 심볼의 pointUnit 가져오기
        const metadataStore = useMetadataStore();
        const metadata = this.currentSymbol ? metadataStore.getMetadata(this.currentSymbol) : undefined;
        const pointUnit = metadata?.pointUnit;
        const basicOverrides = generateAllOverrides(settings, pointUnit);
        const theme = getThemeFromSettings(settings);
        const themeOverrides = generateThemeOverrides(theme);
        return { ...basicOverrides, ...themeOverrides };
    }
    /**
     * 심볼 구독
     */
    subscribeSymbol(symbol, callback) {
        if (!this.webSocketService) {
            console.warn('[ChartManager] WebSocket service not provided');
            return null;
        }
        const subscriptionId = this.webSocketService.subscribe(symbol, callback);
        this.currentSubscriberUID = subscriptionId;
        console.log('[ChartManager] Subscribed to symbol:', symbol, 'ID:', subscriptionId);
        return subscriptionId;
    }
    /**
     * 심볼 구독 해제
     */
    unsubscribeSymbol(symbol) {
        if (this.currentSubscriberUID && this.webSocketService) {
            this.webSocketService.unsubscribe(this.currentSubscriberUID);
            this.currentSubscriberUID = null;
        }
    }
    /**
     * 현재 심볼 정보 가져오기
     */
    getCurrentSymbolInfo() {
        return {
            symbol: this.currentSymbol,
            interval: this.currentInterval,
            lastUpdate: Date.now(),
        };
    }
    /**
     * 차트 위젯 인스턴스 가져오기
     */
    getWidget() {
        return this.widget;
    }
    // ============================================================
    // Bid/Ask 라인 관리
    // ============================================================
    /**
     * Bid/Ask 라인 생성 또는 업데이트
     * @param type - 'bid' 또는 'ask'
     * @param symbol - 심볼
     * @param price - 가격
     * @returns 성공 여부
     */
    async createOrUpdateBidAskLine(type, symbol, price) {
        if (!this.isChartReady()) {
            console.warn('[ChartManager] Chart not ready');
            return false;
        }
        const lineKey = `${type}-${symbol}`;
        return this.withBidAskLineLock(lineKey, async () => {
            const chartApi = this.widget?.chart();
            if (!chartApi || typeof chartApi.createShape !== 'function') {
                console.error('[ChartManager] Chart API not available');
                return false;
            }
            // 차트가 실제로 shape를 생성할 수 있는 상태인지 확인
            // getAllShapes가 사용 가능한지 확인
            if (typeof chartApi.getAllShapes !== 'function') {
                console.warn('[ChartManager] Chart API not fully ready for shape creation');
                return false;
            }
            const existingLine = this.bidAskLines.get(lineKey);
            // 테마에 따른 Bid/Ask 라인 색상 가져오기
            const theme = this.currentSettings ? getThemeFromSettings(this.currentSettings) : 'redBlue';
            const bidAskColors = getBidAskLineColors(theme);
            const lineColor = type === 'bid' ? bidAskColors.bid : bidAskColors.ask;
            const lineStyle = 1; // 점선 (bid/ask 라인은 점선으로 표시)
            const lineWidth = 1;
            // 현재 시간 가져오기 (bid/ask 라인은 전체 차트에 표시되므로 현재 시간 사용)
            const currentTime = Math.floor(Date.now() / 1000);
            if (existingLine) {
                // 기존 라인이 있으면 업데이트 (Shape의 setPoints 사용)
                try {
                    const shapes = chartApi.getAllShapes();
                    if (shapes && Array.isArray(shapes)) {
                        const shape = shapes.find((s) => (s.id || s.getId?.()) === existingLine.shapeId ||
                            existingLine.shapeId === `${type}-${symbol}`);
                        if (shape && typeof shape.setPoints === 'function') {
                            shape.setPoints([{ time: currentTime, price }]);
                            // 가격 업데이트 후 색상도 재적용 (TradingView가 덮어쓰는 것을 방지)
                            try {
                                if (typeof shape.setLineColor === 'function') {
                                    shape.setLineColor(lineColor);
                                }
                                else if (typeof shape.setColor === 'function') {
                                    shape.setColor(lineColor);
                                }
                            }
                            catch (error) {
                                console.warn('[ChartManager] Failed to reapply color on update:', error);
                            }
                            // lock 상태 확인 및 업데이트 (readonly 시세 라인이므로 드래그 불가능하게 함)
                            try {
                                if (typeof shape.setLock === 'function') {
                                    shape.setLock(true);
                                }
                                else if (typeof shape.lock === 'function') {
                                    shape.lock(true);
                                }
                            }
                            catch (error) {
                                // setLock 메서드가 없는 경우 무시 (일부 TradingView 버전에서는 지원하지 않을 수 있음)
                                console.debug('[ChartManager] setLock not available, skipping lock update:', error);
                            }
                            // 찾은 shape의 실제 ID를 가져와서 업데이트
                            const actualShapeId = shape.id ||
                                (typeof shape.getId === 'function' ? shape.getId() : existingLine.shapeId);
                            // 내부 상태 업데이트 (실제 shape ID 포함)
                            this.bidAskLines.set(lineKey, {
                                ...existingLine,
                                price,
                                shapeId: actualShapeId,
                            });
                            return true;
                        }
                    }
                }
                catch (error) {
                    console.warn(`[ChartManager] Failed to update bid/ask line via setPoints, recreating:`, error);
                }
                // 업데이트 실패 시 기존 라인 제거 후 재생성
                this.removeBidAskLine(type, symbol);
            }
            // 새 라인 생성
            // readonly 시세 값을 표시하는 라인이므로 lock: true로 설정하여 드래그 불가능하게 함
            const shapeId = await chartApi.createShape({ time: currentTime, price }, {
                shape: 'horizontal_line',
                lock: true, // lock을 true로 설정하여 드래그 불가능하게 함 (readonly 시세 라인)
                disableSelection: true, // 선택 불가능 (사용자가 클릭할 수 없도록)
                disableSave: true, // 저장하지 않음
                disableUndo: true, // Undo 목록에 추가하지 않음
                zOrder: 'top', // 최상위 레이어
                overrides: {
                    linecolor: lineColor,
                    linewidth: lineWidth,
                    linestyle: lineStyle, // 점선
                    // 우측 가격 축에 가격 레이블 항상 표시
                    showLabel: true,
                    // 추가 설정으로 색상이 덮어쓰이지 않도록 보장
                    'paneProperties.backgroundType': 0,
                },
            });
            // 라인 생성 후 색상 강제 적용 (TradingView가 덮어쓰는 것을 방지)
            if (shapeId) {
                try {
                    // 약간의 지연 후 색상 재적용 (TradingView 초기화 완료 대기)
                    setTimeout(async () => {
                        // 609라인에서 이미 getAllShapes가 함수인지 확인했으므로 옵셔널 체이닝 불필요
                        const shapes = chartApi.getAllShapes();
                        if (shapes && Array.isArray(shapes)) {
                            const shape = shapes.find((s) => (s.id || s.getId?.()) === shapeId ||
                                (typeof s.getId === 'function' && s.getId() === shapeId));
                            if (shape && typeof shape.setLineColor === 'function') {
                                shape.setLineColor(lineColor);
                            }
                            else if (shape && typeof shape.setLineStyle === 'function') {
                                // setLineStyle을 통해 색상과 스타일을 함께 설정
                                shape.setLineStyle(lineStyle);
                                // API가 있는 경우 색상도 설정
                                if (typeof shape.setColor === 'function') {
                                    shape.setColor(lineColor);
                                }
                            }
                        }
                    }, 100);
                }
                catch (error) {
                    console.warn('[ChartManager] Failed to apply color after creation:', error);
                }
            }
            if (shapeId) {
                this.bidAskLines.set(lineKey, {
                    type,
                    symbol,
                    price,
                    shapeId: shapeId,
                });
                return true;
            }
            return false;
        }).catch((error) => {
            console.error(`[ChartManager] Failed to create/update bid/ask line:`, error);
            return false;
        });
    }
    /**
     * 같은 Bid/Ask 라인 키에 대한 비동기 작업을 순차 실행합니다.
     * 느린 환경에서 동시 createShape 호출로 중복 라인이 생성되는 문제를 방지합니다.
     */
    async withBidAskLineLock(lineKey, work) {
        const previousLock = this.bidAskLineLocks.get(lineKey) ?? Promise.resolve();
        let releaseLock;
        const lockPromise = new Promise((resolve) => {
            releaseLock = resolve;
        });
        const nextLock = previousLock.then(() => lockPromise);
        this.bidAskLineLocks.set(lineKey, nextLock);
        await previousLock;
        try {
            return await work();
        }
        finally {
            releaseLock();
            if (this.bidAskLineLocks.get(lineKey) === nextLock) {
                this.bidAskLineLocks.delete(lineKey);
            }
        }
    }
    /**
     * Bid/Ask 라인 제거
     * @param type - 'bid' 또는 'ask'
     * @param symbol - 심볼
     * @returns 성공 여부
     */
    removeBidAskLine(type, symbol) {
        const lineKey = `${type}-${symbol}`;
        const existingLine = this.bidAskLines.get(lineKey);
        if (!existingLine) {
            return false;
        }
        // widget이 없거나 초기화되지 않은 경우 라인만 삭제
        if (!this.widget || !this.isInitialized) {
            this.bidAskLines.delete(lineKey);
            return true;
        }
        try {
            const chartApi = this.widget.chart();
            if (chartApi && typeof chartApi.removeEntity === 'function') {
                try {
                    // getAllShapes()로 실제 shape를 찾아서 제거
                    // shapeId가 저장되어 있지만, 실제 shape를 찾아서 제거하는 것이 더 안전함
                    const shapes = chartApi.getAllShapes?.();
                    if (shapes && Array.isArray(shapes)) {
                        // 저장된 shapeId와 일치하는 shape 찾기
                        const shapeToRemove = shapes.find((s) => {
                            const shapeId = s.id || (typeof s.getId === 'function' ? s.getId() : null);
                            return shapeId === existingLine.shapeId;
                        });
                        if (shapeToRemove) {
                            const shapeId = shapeToRemove.id ||
                                (typeof shapeToRemove.getId === 'function' ? shapeToRemove.getId() : null);
                            if (shapeId) {
                                chartApi.removeEntity(shapeId);
                            }
                        }
                        else {
                            // shapeId로 직접 제거 시도 (fallback)
                            if (existingLine.shapeId && existingLine.shapeId !== `${type}-${symbol}`) {
                                chartApi.removeEntity(existingLine.shapeId);
                            }
                        }
                    }
                    else {
                        // getAllShapes()가 없는 경우 shapeId로 직접 제거 시도
                        if (existingLine.shapeId && existingLine.shapeId !== `${type}-${symbol}`) {
                            chartApi.removeEntity(existingLine.shapeId);
                        }
                    }
                }
                catch (error) {
                    // 차트가 이미 destroy된 경우 무시
                    console.warn(`[ChartManager] Failed to remove entity, chart may be destroyed:`, error);
                }
            }
        }
        catch (error) {
            // 차트가 이미 destroy되어 chart() 호출 자체가 실패한 경우
            console.warn(`[ChartManager] Failed to get chart API, chart may be destroyed:`, error);
        }
        this.bidAskLines.delete(lineKey);
        return true;
    }
    /**
     * 심볼의 모든 Bid/Ask 라인 제거
     * @param symbol - 심볼
     */
    removeAllBidAskLinesForSymbol(symbol) {
        this.removeBidAskLine('bid', symbol);
        this.removeBidAskLine('ask', symbol);
    }
    /**
     * 모든 Bid/Ask 라인 제거
     */
    removeAllBidAskLines() {
        // widget이 이미 destroy된 경우 라인만 삭제
        if (!this.widget) {
            this.bidAskLines.clear();
            return;
        }
        const linesToRemove = Array.from(this.bidAskLines.keys());
        for (const lineKey of linesToRemove) {
            const parts = lineKey.split('-');
            if (parts.length >= 2) {
                const type = parts[0];
                const symbol = parts.slice(1).join('-');
                this.removeBidAskLine(type, symbol);
            }
        }
    }
    /**
     * 모든 Bid/Ask 라인의 색상을 테마에 맞게 업데이트
     */
    async updateAllBidAskLineColors() {
        if (!this.widget || !this.isInitialized || this.bidAskLines.size === 0) {
            return;
        }
        // 테마에 따른 색상 가져오기
        const theme = this.currentSettings ? getThemeFromSettings(this.currentSettings) : 'redBlue';
        const bidAskColors = getBidAskLineColors(theme);
        // 모든 bid/ask 라인 색상 업데이트
        for (const [lineKey, lineData] of this.bidAskLines.entries()) {
            const lineColor = lineData.type === 'bid' ? bidAskColors.bid : bidAskColors.ask;
            // 기존 라인 재생성 (색상 변경을 위해)
            await this.createOrUpdateBidAskLine(lineData.type, lineData.symbol, lineData.price);
        }
    }
    /**
     * 초기화 상태 확인
     */
    isChartReady() {
        return this.isInitialized && this.widget !== null;
    }
    /**
     * 차트 로드 완료 후에만 적용 가능한 설정들
     * 차트 위젯이 완전히 초기화된 후 실행되는 설정
     */
    applyPostLoadSettings() {
        if (!this.widget || !this.currentSettings) {
            console.warn('[ChartManager] Cannot apply post-load settings: widget or settings not available');
            return;
        }
        try {
            // 차트가 완전히 로드된 후 설정 재적용
            // console.log('[ChartManager] Applying post-load settings...');
            // 현재 설정을 overrides로 변환하여 적용
            const overrides = this.convertSettingsToOverrides(this.currentSettings);
            this.applyChartOverrides(overrides);
            // 테마 배경 업데이트
            this.updateThemeBackground();
        }
        catch (error) {
            console.error('[ChartManager] Failed to apply post-load settings:', error);
        }
    }
    /**
     * 테마 변경에 따른 차트 배경 업데이트
     */
    updateThemeBackground() {
        if (!this.widget || !this.isInitialized) {
            console.warn('[ChartManager] Chart not initialized, cannot update theme background');
            return;
        }
        try {
            // 현재 테마에 따른 배경 색상 overrides 생성
            const themeBackgroundOverrides = generateThemeBackgroundOverrides();
            // 차트에 배경 색상 적용
            this.applyChartOverrides(themeBackgroundOverrides);
        }
        catch (error) {
            console.error('[ChartManager] Failed to update theme background:', error);
        }
    }
    // ============================================================
    // Order Line 관련 메서드
    // ============================================================
    /**
     * Hex 색상을 rgba 형식으로 변환 (투명도 추가)
     * @param hex - Hex 색상 코드 (#RRGGBB 또는 #RGB)
     * @param opacity - 투명도 (0.0 ~ 1.0)
     * @returns rgba 색상 문자열
     */
    addOpacityToColor(hex, opacity) {
        // # 제거
        const cleanHex = hex.replace('#', '');
        // RGB 값 추출
        let r, g, b;
        if (cleanHex.length === 3) {
            // 짧은 형식 (#RGB)
            r = parseInt(cleanHex[0] + cleanHex[0], 16);
            g = parseInt(cleanHex[1] + cleanHex[1], 16);
            b = parseInt(cleanHex[2] + cleanHex[2], 16);
        }
        else {
            // 긴 형식 (#RRGGBB)
            r = parseInt(cleanHex.substring(0, 2), 16);
            g = parseInt(cleanHex.substring(2, 4), 16);
            b = parseInt(cleanHex.substring(4, 6), 16);
        }
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    /**
     * 주문 라인 색상 결정
     * @param orderLine - 주문 라인 정보
     * @returns 주문 라인 색상 (hex)
     */
    getOrderLineColor(orderLine) {
        // TP/SL 라인은 고정 색상 사용
        if (orderLine.type === 'takeProfit') {
            return this.orderLineStyle.tpColor;
        }
        if (orderLine.type === 'stopLoss') {
            return this.orderLineStyle.slColor;
        }
        // 메인 주문 라인(market, limit, stop, stopLimit)은 매수/매도 색상 사용
        return orderLine.side === 'buy' ? this.orderLineStyle.buyColor : this.orderLineStyle.sellColor;
    }
    /**
     * 주문 타입 텍스트 변환
     * @param type - 주문 타입
     * @returns 주문 타입 텍스트
     */
    getOrderTypeText(type) {
        const typeMap = {
            market: '시장가',
            limit: '지정가',
            stop: 'Stop',
            stopLimit: 'Stop Limit',
            takeProfit: 'TP',
            stopLoss: 'SL',
        };
        return typeMap[type] || type;
    }
    /**
     * 주문 라인 스타일 설정
     * @param style - 주문 라인 스타일 (부분 업데이트 가능)
     */
    setOrderLineStyle(style) {
        this.orderLineStyle = {
            ...this.orderLineStyle,
            ...style,
        };
        console.log('[ChartManager] Order line style updated:', this.orderLineStyle);
    }
    /**
     * 주문 라인 스타일 조회
     * @returns 주문 라인 스타일 설정
     */
    getOrderLineStyle() {
        return { ...this.orderLineStyle };
    }
    /**
     * 주문 라인 생성
     * @param orderLine - 생성할 주문 라인 정보
     * @returns 생성된 주문 라인 ID (실패 시 null)
     */
    async createOrderLine(orderLine) {
        if (!this.widget || !this.isInitialized) {
            console.warn('[ChartManager] Cannot create order line: chart not initialized');
            return null;
        }
        try {
            // 이미 존재하는 order line인지 체크 (중복 생성 방지)
            if (this.orderLines.has(orderLine.id)) {
                return orderLine.id;
            }
            // 가격 유효성 검증
            if (!orderLine.price || orderLine.price <= 0) {
                return null;
            }
            // widget.activeChart() 또는 widget.chart()를 사용하여 실제 차트 API 접근
            const chartApi = typeof this.widget.activeChart === 'function'
                ? this.widget.activeChart()
                : this.widget.chart();
            if (!chartApi) {
                console.warn('[ChartManager] Chart instance not available');
                return null;
            }
            // TradingView Widget API: createShape()로 수평선 그리기
            if (typeof chartApi.createShape !== 'function') {
                console.error('[ChartManager] ❌ createShape not available');
                return null;
            }
            // 주문 타입에 따른 색상 결정
            const baseLineColor = this.getOrderLineColor(orderLine);
            // 투명도 적용 (기본 0.3)
            const lineColor = this.addOpacityToColor(baseLineColor, 0.3);
            // TP/SL 라인은 점선, 메인 라인은 실선
            const isTpSlLine = orderLine.type === 'takeProfit' || orderLine.type === 'stopLoss';
            const lineStyle = isTpSlLine ? 1 : 0; // 0 = 실선, 1 = 점선
            // 현재 차트 시간 가져오기
            const currentTime = Math.floor(Date.now() / 1000);
            // TradingView createShape() API로 수평선 생성
            const shapeResult = await chartApi.createShape({ time: currentTime, price: orderLine.price }, {
                shape: 'horizontal_line',
                lock: true,
                disableSelection: true,
                disableSave: true,
                disableUndo: false,
                zOrder: 'top',
                showInObjectsTree: false,
                overrides: {
                    // === 라인 스타일 ===
                    linecolor: lineColor,
                    linewidth: 1,
                    linestyle: lineStyle, // TP/SL은 점선, 메인은 실선
                    // === 라벨 표시 ===
                    showLabel: false, // OrderLineLabel.vue 컴포넌트가 라벨을 표시하므로 숨김
                    text: '',
                    // === 라벨 텍스트 스타일 ===
                    textcolor: '#FFFFFF',
                    fontsize: 12,
                    bold: true,
                    // === 라벨 배경 ===
                    backgroundColor: lineColor,
                    backgroundTransparency: 0,
                    borderColor: lineColor,
                    // === 라벨 위치 ===
                    horzLabelsAlign: 'right',
                    vertLabelsAlign: 'middle',
                },
            });
            // createShape()가 반환하는 값 확인
            // shapeId는 문자열일 수도 있고, Shape 객체일 수도 있음
            const shapeId = typeof shapeResult === 'string' ? shapeResult : shapeResult?.id || shapeResult;
            const actualShapeObject = typeof shapeResult === 'object' && shapeResult !== null ? shapeResult : null;
            // Shape를 OrderLine처럼 관리하기 위한 어댑터 객체 생성
            const shapeAdapter = {
                shapeId, // Shape ID 저장
                chartApi, // Chart API 참조 저장
                remove: () => {
                    if (shapeId) {
                        chartApi.removeEntity(shapeId);
                    }
                },
                setPrice: (_newPrice) => {
                    console.warn('[ChartManager] Shape price update requires recreation');
                },
                setQuantity: (_quantity) => {
                    console.warn('[ChartManager] Shape quantity update not supported');
                },
                setText: (_text) => {
                    console.warn('[ChartManager] Shape text update not supported');
                },
                getText: () => '', // OrderLineLabel.vue 컴포넌트가 라벨을 표시하므로 빈 문자열
                getPoints: () => {
                    const api = chartApi;
                    // 방법 1: createShape()가 반환한 실제 Shape 객체 사용 (가장 확실)
                    // actualShapeObject는 createOrderLine 함수의 클로저 변수
                    if (actualShapeObject && typeof actualShapeObject.getPoints === 'function') {
                        try {
                            const points = actualShapeObject.getPoints();
                            return points;
                        }
                        catch (e) {
                            console.warn('[ChartManager] actualShapeObject.getPoints failed:', e);
                        }
                    }
                    // 방법 2: getShapeById()로 실제 Shape 객체 가져오기 (원 브랜치에서 사용)
                    if (typeof api.getShapeById === 'function') {
                        try {
                            const shape = api.getShapeById(shapeId);
                            if (shape && typeof shape.getPoints === 'function') {
                                const points = shape.getPoints();
                                return points;
                            }
                        }
                        catch (e) {
                            console.warn('[ChartManager] getShapeById failed:', e);
                        }
                    }
                    // 방법 3: getAllShapes()를 통해 Shape 객체 찾기 (원 브랜치와 동일)
                    if (typeof api.getAllShapes === 'function') {
                        try {
                            const shapes = api.getAllShapes();
                            for (const shape of shapes || []) {
                                const shapeIdFromShape = shape?.id || (typeof shape?.getId === 'function' ? shape.getId() : null);
                                if (shapeIdFromShape === shapeId && typeof shape?.getPoints === 'function') {
                                    return shape.getPoints();
                                }
                            }
                        }
                        catch (e) {
                            console.warn('[ChartManager] getAllShapes failed:', e);
                        }
                    }
                    return null;
                },
            };
            // 주문 라인 정보 저장 (원 브랜치와 동일하게 shapeAdapter만 저장)
            // shapeAdapter.getPoints()가 호출될 때마다 getAllShapes()로 Shape를 찾아서 getPoints() 호출
            this.orderLines.set(orderLine.id, {
                orderLine: { ...orderLine },
                priceLine: shapeAdapter,
            });
            return orderLine.id;
        }
        catch (error) {
            console.error('[ChartManager] Failed to create order line:', error);
            return null;
        }
    }
    /**
     * 주문 라인 업데이트
     * @param orderId - 업데이트할 주문 ID
     * @param updates - 업데이트할 필드들
     * @returns 성공 여부
     */
    updateOrderLine(orderId, updates) {
        const orderLineData = this.orderLines.get(orderId);
        if (!orderLineData) {
            return false;
        }
        try {
            const { orderLine, priceLine } = orderLineData;
            // 주문 정보 업데이트
            const updatedOrderLine = {
                ...orderLine,
                ...updates,
            };
            // 가격이 변경된 경우 Shape의 setPoints()를 사용하여 업데이트
            if (updates.price !== undefined && updates.price !== orderLine.price) {
                if (!this.widget || !this.isInitialized) {
                    console.warn('[ChartManager] Chart not ready for price update');
                    return false;
                }
                const chartApi = typeof this.widget.activeChart === 'function'
                    ? this.widget.activeChart()
                    : this.widget.chart();
                if (!chartApi) {
                    console.warn('[ChartManager] Chart API not available');
                    return false;
                }
                // shapeId 가져오기 (orderLineData에 저장된 shapeId 사용)
                const shapeId = priceLine.shapeId;
                if (!shapeId) {
                    console.warn('[ChartManager] Shape ID not found for order line:', orderId);
                    return false;
                }
                // getShapeById()로 실제 Shape 객체 가져오기
                const api = chartApi;
                if (typeof api.getShapeById === 'function') {
                    try {
                        const shape = api.getShapeById(shapeId);
                        if (shape && typeof shape.setPoints === 'function') {
                            // 현재 시간 가져오기
                            const currentTime = Math.floor(Date.now() / 1000);
                            // setPoints()로 가격 업데이트
                            shape.setPoints([{ time: currentTime, price: updates.price }]);
                        }
                        else {
                            console.warn('[ChartManager] Shape not found or setPoints not available:', {
                                orderId,
                                shapeId,
                                hasShape: !!shape,
                                hasSetPoints: shape && typeof shape.setPoints === 'function',
                            });
                            return false;
                        }
                    }
                    catch (error) {
                        console.warn('[ChartManager] Failed to update shape price via setPoints:', error);
                        return false;
                    }
                }
                else {
                    console.warn('[ChartManager] getShapeById not available');
                    return false;
                }
            }
            // 업데이트된 정보 저장
            this.orderLines.set(orderId, {
                orderLine: updatedOrderLine,
                priceLine,
            });
            return true;
        }
        catch (error) {
            console.error('[ChartManager] Failed to update order line:', error);
            return false;
        }
    }
    /**
     * 주문 라인 제거
     * @param orderId - 제거할 주문 ID
     * @returns 성공 여부
     */
    removeOrderLine(orderId) {
        const orderLineData = this.orderLines.get(orderId);
        if (!orderLineData) {
            return false;
        }
        try {
            const { priceLine } = orderLineData;
            // TradingView에서 Shape 제거
            if (priceLine && typeof priceLine.remove === 'function') {
                priceLine.remove();
            }
            else if (typeof priceLine.removeEntity === 'function') {
                priceLine.removeEntity(priceLine.shapeId);
            }
            // 내부 맵에서 제거
            this.orderLines.delete(orderId);
            return true;
        }
        catch (error) {
            console.error('[ChartManager] Failed to remove order line:', error);
            return false;
        }
    }
    /**
     * 모든 주문 라인 제거
     */
    removeAllOrderLines() {
        const orderIds = Array.from(this.orderLines.keys());
        orderIds.forEach((orderId) => {
            this.removeOrderLine(orderId);
        });
    }
    /**
     * 주문 라인 조회
     * @param orderId - 주문 라인 ID
     * @returns 주문 라인 정보 (없으면 null)
     */
    getOrderLine(orderId) {
        const orderLineData = this.orderLines.get(orderId);
        return orderLineData ? orderLineData.orderLine : null;
    }
    /**
     * 모든 주문 라인 목록 조회
     * @returns 모든 주문 라인 목록
     */
    getAllOrderLines() {
        const lines = [];
        this.orderLines.forEach(({ orderLine }) => {
            lines.push(orderLine);
        });
        return lines;
    }
    /**
     * 심볼별 주문 라인 목록 조회
     * @param symbol - 심볼
     * @returns 주문 라인 목록
     */
    getOrderLinesBySymbol(symbol) {
        const lines = [];
        this.orderLines.forEach(({ orderLine }) => {
            if (orderLine.symbol === symbol) {
                lines.push(orderLine);
            }
        });
        return lines;
    }
    /**
     * 가격을 Y 좌표로 변환
     * @param price - 가격
     * @returns Y 좌표 (실패 시 null)
     */
    priceToYCoordinate(price) {
        if (!this.widget || !this.isInitialized) {
            return null;
        }
        try {
            const chartApi = typeof this.widget.activeChart === 'function'
                ? this.widget.activeChart()
                : this.widget.chart();
            if (!chartApi) {
                return null;
            }
            // PriceScale 가져오기 (getPriceFromClick과 동일한 방식 사용)
            let priceScale = null;
            if (typeof chartApi.priceScale === 'function') {
                priceScale = chartApi.priceScale('right');
            }
            else if (typeof chartApi.getPanes === 'function') {
                const panes = chartApi.getPanes();
                if (panes && panes.length > 0) {
                    const mainPane = panes[0];
                    if (typeof mainPane.getRightPriceScales === 'function') {
                        const scales = mainPane.getRightPriceScales();
                        if (scales && scales.length > 0) {
                            priceScale = scales[0];
                        }
                    }
                }
            }
            if (!priceScale) {
                return null;
            }
            // priceToCoordinate 메서드 사용
            if (typeof priceScale.priceToCoordinate === 'function') {
                const yCoordinate = priceScale.priceToCoordinate(price);
                if (yCoordinate !== null && yCoordinate !== undefined) {
                    return yCoordinate;
                }
            }
            // priceToCoordinate가 없거나 null을 반환하는 경우
            // coordinateToPrice를 사용해서 역변환 시도 (이진 탐색)
            if (typeof priceScale.coordinateToPrice === 'function') {
                const container = document.getElementById(this.containerId);
                if (container) {
                    const chartHeight = container.clientHeight;
                    // 먼저 차트의 가격 범위 확인 (상단과 하단의 가격)
                    const topPrice = priceScale.coordinateToPrice(0);
                    const bottomPrice = priceScale.coordinateToPrice(chartHeight);
                    if (topPrice === null || bottomPrice === null) {
                        return null;
                    }
                    // 가격이 차트 범위를 벗어나면 null 반환
                    const minPrice = Math.min(topPrice, bottomPrice);
                    const maxPrice = Math.max(topPrice, bottomPrice);
                    if (price < minPrice || price > maxPrice) {
                        return null;
                    }
                    // 가격이 높을수록 Y 좌표가 낮은지 확인 (일반적인 차트)
                    const isPriceInverted = topPrice > bottomPrice;
                    let low = 0;
                    let high = chartHeight;
                    let bestY = null;
                    let bestDiff = Infinity;
                    // 이진 탐색으로 가격에 해당하는 Y 좌표 찾기
                    for (let i = 0; i < 30; i++) {
                        const midY = (low + high) / 2;
                        const midPrice = priceScale.coordinateToPrice(midY);
                        if (midPrice === null || midPrice === undefined) {
                            break;
                        }
                        const diff = Math.abs(midPrice - price);
                        if (diff < bestDiff) {
                            bestDiff = diff;
                            bestY = midY;
                        }
                        // 가격이 높을수록 Y 좌표가 낮은 경우 (일반적인 차트)
                        if (isPriceInverted) {
                            if (midPrice > price) {
                                low = midY; // 가격이 높으면 Y 좌표를 낮춰야 함
                            }
                            else {
                                high = midY; // 가격이 낮으면 Y 좌표를 높여야 함
                            }
                        }
                        else {
                            // 가격이 높을수록 Y 좌표가 높은 경우 (역차트)
                            if (midPrice > price) {
                                high = midY;
                            }
                            else {
                                low = midY;
                            }
                        }
                        // 충분히 가까우면 중단
                        if (diff < 0.00001) {
                            bestY = midY;
                            break;
                        }
                    }
                    if (bestY !== null && bestDiff < 0.001) {
                        return bestY;
                    }
                }
            }
            // 다른 메서드 시도 (priceToPixel 등)
            for (const name of ['priceToPixel', 'priceToY']) {
                if (typeof priceScale[name] === 'function') {
                    const yCoordinate = priceScale[name](price);
                    if (yCoordinate !== null && yCoordinate !== undefined) {
                        return yCoordinate;
                    }
                }
            }
            return null;
        }
        catch (error) {
            return null;
        }
    }
    /**
     * 차트의 가격 범위 가져오기
     * @returns 차트의 상단/하단 가격 및 높이 정보 (없으면 null)
     */
    getPriceRange() {
        if (!this.widget || !this.isInitialized) {
            return null;
        }
        try {
            const chartApi = typeof this.widget.activeChart === 'function'
                ? this.widget.activeChart()
                : this.widget.chart();
            if (!chartApi) {
                return null;
            }
            // PriceScale 가져오기
            let priceScale = null;
            if (typeof chartApi.priceScale === 'function') {
                priceScale = chartApi.priceScale('right');
            }
            else if (typeof chartApi.getPanes === 'function') {
                const panes = chartApi.getPanes();
                if (panes && panes.length > 0) {
                    const mainPane = panes[0];
                    if (typeof mainPane.getRightPriceScales === 'function') {
                        const scales = mainPane.getRightPriceScales();
                        if (scales && scales.length > 0) {
                            priceScale = scales[0];
                        }
                    }
                    else if (typeof mainPane.getMainSourcePriceScale === 'function') {
                        priceScale = mainPane.getMainSourcePriceScale();
                    }
                }
            }
            if (!priceScale || typeof priceScale.coordinateToPrice !== 'function') {
                return null;
            }
            const container = document.getElementById(this.containerId);
            if (!container) {
                return null;
            }
            const height = container.clientHeight;
            const topPrice = priceScale.coordinateToPrice(0);
            const bottomPrice = priceScale.coordinateToPrice(height);
            if (topPrice === null || bottomPrice === null) {
                return null;
            }
            return { topPrice, bottomPrice, height };
        }
        catch (error) {
            return null;
        }
    }
    /**
     * 차트 범위 밖 가격의 Y 좌표 계산
     * @param price - 계산할 가격
     * @returns 계산된 Y 좌표 (범위 내이거나 계산 불가능하면 null)
     */
    calculateOutOfRangeYCoordinate(price) {
        const priceRange = this.getPriceRange();
        if (!priceRange) {
            return null;
        }
        const { topPrice, bottomPrice, height } = priceRange;
        const isInverted = topPrice < bottomPrice;
        const highestPrice = isInverted ? bottomPrice : topPrice;
        const lowestPrice = isInverted ? topPrice : bottomPrice;
        // 범위 내 가격은 null 반환 (priceToYCoordinate 사용)
        if (price >= lowestPrice && price <= highestPrice) {
            return null;
        }
        // 범위 밖 가격 계산
        if (price > highestPrice) {
            // 차트 위쪽 밖 (높은 가격): 음수 Y 좌표
            const priceDiff = price - highestPrice;
            const rangeDiff = highestPrice - lowestPrice;
            const ratio = rangeDiff > 0 ? priceDiff / rangeDiff : 0;
            return -height * ratio;
        }
        else if (price < lowestPrice) {
            // 차트 아래쪽 밖 (낮은 가격): 컨테이너 높이보다 큰 Y 좌표
            const priceDiff = lowestPrice - price;
            const rangeDiff = highestPrice - lowestPrice;
            const ratio = rangeDiff > 0 ? priceDiff / rangeDiff : 0;
            return height + height * ratio;
        }
        return null;
    }
    /**
     * 주문 라인의 기본 색상(hex) 조회
     * 라인/라벨 색상을 동일한 로직으로 유지하기 위해 제공합니다.
     * @param orderId - 주문 라인 ID
     * @returns 주문 라인 기본 색상(hex) (없으면 null)
     */
    getOrderLineBaseColorById(orderId) {
        const orderLineData = this.orderLines.get(orderId);
        if (!orderLineData) {
            return null;
        }
        return this.getOrderLineColor(orderLineData.orderLine);
    }
    /**
     * 특정 심볼의 모든 주문 라인 제거
     * @param symbol - 심볼
     */
    removeAllOrderLinesForSymbol(symbol) {
        const orderIdsToRemove = [];
        this.orderLines.forEach(({ orderLine }, orderId) => {
            if (orderLine.symbol === symbol) {
                orderIdsToRemove.push(orderId);
            }
        });
        orderIdsToRemove.forEach((orderId) => {
            this.removeOrderLine(orderId);
        });
    }
    /**
     * 특정 Y 좌표 위치의 주문 라인 찾기
     * @param clickY - 클릭한 Y 좌표
     * @param tolerance - 허용 오차 (기본값: 5px)
     * @returns 찾은 주문 라인 또는 null
     */
    findOrderLineAtPosition(clickY, tolerance = 5) {
        const orderLines = this.getAllOrderLines();
        for (const orderLine of orderLines) {
            const lineY = this.priceToYCoordinate(orderLine.price);
            if (lineY !== null && Math.abs(lineY - clickY) <= tolerance) {
                return orderLine;
            }
        }
        return null;
    }
    /**
     * 클릭 이벤트에서 가격 가져오기
     * @param event - 마우스 이벤트
     * @returns 가격 또는 null
     */
    getPriceFromClick(event) {
        if (!this.widget || !this.isInitialized) {
            return null;
        }
        try {
            const chartApi = typeof this.widget.activeChart === 'function'
                ? this.widget.activeChart()
                : this.widget.chart();
            if (!chartApi) {
                return null;
            }
            // PriceScale 가져오기
            let priceScale = null;
            if (typeof chartApi.priceScale === 'function') {
                priceScale = chartApi.priceScale('right');
            }
            else if (typeof chartApi.getPanes === 'function') {
                const panes = chartApi.getPanes();
                if (panes && panes.length > 0) {
                    const mainPane = panes[0];
                    if (typeof mainPane.getRightPriceScales === 'function') {
                        const scales = mainPane.getRightPriceScales();
                        if (scales && scales.length > 0) {
                            priceScale = scales[0];
                        }
                    }
                }
            }
            if (!priceScale || typeof priceScale.coordinateToPrice !== 'function') {
                return null;
            }
            // 컨테이너 내 클릭 Y 좌표 계산
            const container = document.getElementById(this.containerId);
            if (!container) {
                return null;
            }
            const containerRect = container.getBoundingClientRect();
            const clickY = event.clientY - containerRect.top;
            // Y 좌표를 가격으로 변환
            return priceScale.coordinateToPrice(clickY);
        }
        catch (error) {
            console.warn('[ChartManager] Failed to get price from click:', error);
            return null;
        }
    }
    /**
     * Y 좌표를 가격으로 변환
     * @param yCoordinate - 차트 컨테이너 내 Y 좌표 (px)
     * @returns 가격 또는 null
     */
    getPriceFromYCoordinate(yCoordinate) {
        if (!this.widget || !this.isInitialized) {
            return null;
        }
        try {
            const chartApi = typeof this.widget.activeChart === 'function'
                ? this.widget.activeChart()
                : this.widget.chart();
            if (!chartApi) {
                return null;
            }
            // PriceScale 가져오기
            let priceScale = null;
            if (typeof chartApi.priceScale === 'function') {
                priceScale = chartApi.priceScale('right');
            }
            else if (typeof chartApi.getPanes === 'function') {
                const panes = chartApi.getPanes();
                if (panes && panes.length > 0) {
                    const mainPane = panes[0];
                    if (typeof mainPane.getRightPriceScales === 'function') {
                        const scales = mainPane.getRightPriceScales();
                        if (scales && scales.length > 0) {
                            priceScale = scales[0];
                        }
                    }
                }
            }
            if (!priceScale || typeof priceScale.coordinateToPrice !== 'function') {
                return null;
            }
            // Y 좌표를 가격으로 변환
            return priceScale.coordinateToPrice(yCoordinate);
        }
        catch (error) {
            console.warn('[ChartManager] Failed to get price from Y coordinate:', error);
            return null;
        }
    }
    /**
     * Chart API 가져오기
     * @returns Chart API 또는 null
     */
    getChartApi() {
        if (!this.widget || !this.isInitialized) {
            return null;
        }
        try {
            return typeof this.widget.activeChart === 'function'
                ? this.widget.activeChart()
                : this.widget.chart();
        }
        catch (error) {
            console.warn('[ChartManager] Failed to get chart API:', error);
            return null;
        }
    }
    /**
     * 같은 orderNo를 가진 모든 주문 라인 ID 조회
     * @param orderNo - 주문 번호
     * @returns 주문 라인 ID 배열
     */
    getOrderLineIdsByOrderNo(orderNo) {
        return Array.from(this.orderLines.values())
            .filter((data) => data.orderLine.orderNo === orderNo)
            .map((data) => data.orderLine.id);
    }
    /**
     * Shape에서 현재 가격 가져오기 (드래그로 이동 시)
     * @param orderId - 주문 ID
     * @returns 현재 가격 또는 null
     */
    getOrderLineCurrentPrice(orderId) {
        const orderLineData = this.orderLines.get(orderId);
        if (!orderLineData) {
            return null;
        }
        try {
            const { priceLine, orderLine } = orderLineData;
            // 드래그 가능한 라인만 체크 (원 브랜치와 동일)
            if (!orderLine.draggable) {
                return null;
            }
            // Shape 타입 체크 (원 브랜치와 동일하게 구현)
            // priceLine은 shapeAdapter이고, getPoints()가 호출될 때마다 getAllShapes()로 Shape를 찾음
            if ('getPoints' in priceLine && typeof priceLine.getPoints === 'function') {
                const points = priceLine.getPoints();
                if (points && points.length > 0) {
                    // horizontal_line은 하나의 점만 있음
                    const currentPrice = points[0].price;
                    return currentPrice;
                }
            }
            return null;
        }
        catch (error) {
            console.error('[ChartManager] ❌ Failed to get current price:', error);
            return null;
        }
    }
    /**
     * 단일 주문 라인 가격 동기화 (드래그 완료 시)
     * @param orderId - 주문 ID
     * @returns 가격이 변경되었는지 여부
     */
    syncSingleOrderLinePrice(orderId) {
        const orderLineData = this.orderLines.get(orderId);
        if (!orderLineData) {
            return false;
        }
        const currentPrice = this.getOrderLineCurrentPrice(orderId);
        if (currentPrice !== null && Math.abs(currentPrice - orderLineData.orderLine.price) > 0.00001) {
            // 가격이 변경됨
            const oldPrice = orderLineData.orderLine.price;
            orderLineData.orderLine.price = currentPrice;
            return true;
        }
        return false;
    }
    /**
     * 모든 주문 라인의 가격 동기화
     * 라벨 드래그로 인한 가격 변경 감지용
     * @returns 가격이 변경된 주문 라인 ID 배열
     */
    syncOrderLinePrices() {
        const changedOrderIds = [];
        this.orderLines.forEach((orderLineData, orderId) => {
            if (this.syncSingleOrderLinePrice(orderId)) {
                changedOrderIds.push(orderId);
            }
        });
        return changedOrderIds;
    }
    /**
     * 차트 정리 및 해제
     */
    destroy() {
        if (this.currentSymbol && this.currentSubscriberUID) {
            this.unsubscribeSymbol(this.currentSymbol);
        }
        this.removeAllBidAskLines();
        // 위젯이 초기화된 상태에서만 chart() 메서드 호출
        if (this.widget && this.isInitialized) {
            try {
                if (typeof this.widget.chart === 'function') {
                    const chart = this.widget.chart();
                    if (chart && typeof chart.remove === 'function') {
                        chart.remove();
                    }
                }
            }
            catch (error) {
                console.warn('[ChartManager] Chart destroy warning:', error);
            }
        }
        this.widget = null;
        this.isInitialized = false;
        this.currentSymbol = '';
        this.currentInterval = '1';
        this.currentSubscriberUID = null;
        this.bidAskLineLocks.clear();
    }
}
//# sourceMappingURL=ChartManager.js.map